import { NavLink } from 'react-router-dom';
import { LayoutDashboard, FileText, Settings as SettingsIcon, Shield, Building2, Package, X, ShieldCheck, HelpCircle, Boxes, LifeBuoy, Ticket, Layers } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import type { messages as MSG } from '@/lib/i18n.messages';
import { Logo } from './Logo';
import { cn } from '@/lib/utils';

type Key = keyof typeof MSG;

const clientLinks: { to: string; key: Key; icon: any }[] = [
  { to: '/dashboard',  key: 'nav.dashboard',  icon: LayoutDashboard },
  { to: '/toolkits',   key: 'nav.toolkits',   icon: Boxes },
  { to: '/campaigns',  key: 'nav.campaigns',  icon: Layers },
  { to: '/documents',  key: 'nav.documents',  icon: FileText },
  { to: '/support',    key: 'nav.support',    icon: LifeBuoy },
  { to: '/settings',   key: 'nav.settings',   icon: SettingsIcon },
  { to: '/help',       key: 'nav.help',       icon: HelpCircle },
];

const adminLinks: { to: string; key: Key; icon: any }[] = [
  { to: '/admin',           key: 'nav.admin.home',     icon: Shield },
  { to: '/admin/orgs',      key: 'nav.admin.orgs',     icon: Building2 },
  { to: '/admin/globals',   key: 'nav.admin.globals',  icon: ShieldCheck },
  { to: '/admin/releases',  key: 'nav.admin.releases', icon: Package },
  { to: '/admin/tickets',   key: 'nav.admin.tickets',  icon: Ticket },
];

interface SidebarProps {
  /** Mobile drawer open state (controlled). Desktop ignores this. */
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { isGlobalAdmin } = useAuth();
  const { t } = useT();

  const content = (
    <>
      <div className="flex h-20 items-center justify-between border-b border-ink-800 px-5 dark:border-border/30">
        <div className="flex min-w-0 items-center gap-1.5">
          <Logo variant="lockup" tone="light" className="h-11 max-w-[220px]" />
          <span className="shrink-0 self-end pb-1 text-[0.65rem] font-medium tracking-wide text-ink-400 dark:text-muted-foreground">
            ({t('nav.brand.beta')})
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-ink-300 hover:text-white lg:hidden" aria-label={t('common.close')}>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3 text-sm">
        <SectionLabel>{t('nav.section.client')}</SectionLabel>
        {clientLinks.map(l => (
          <Item
            key={l.to}
            to={l.to}
            label={t(l.key)}
            icon={l.icon}
            onNavigate={onClose}
          />
        ))}

        {isGlobalAdmin && (
          <>
            <SectionLabel className="mt-6">{t('nav.section.admin')}</SectionLabel>
            {adminLinks.map(l => <Item key={l.to} to={l.to} label={t(l.key)} icon={l.icon} onNavigate={onClose} />)}
          </>
        )}
      </nav>

      <div className="mt-auto border-t border-ink-800 dark:border-border/30 dark:bg-card/30">
        <div className="p-4 text-xs text-ink-300">
          <div className="font-display tracking-wide text-ink-100 dark:text-foreground">CS Tools</div>
          <div className="mt-1 dark:text-muted-foreground">© {new Date().getFullYear()} · {t('nav.brand.tagline')}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop: static sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-ink-900 text-ink-50 dark:border-border/30 dark:bg-card lg:flex">
        {content}
      </aside>

      {/* Mobile: drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-ink-900 text-ink-50 shadow-2xl transition-transform dark:bg-card lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={!open}
      >
        {content}
      </aside>
    </>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('px-3 pb-1.5 pt-2 text-[0.65rem] font-semibold uppercase tracking-widest text-ink-400 dark:text-muted-foreground', className)}>
      {children}
    </div>
  );
}

function Item({ to, label, icon: Icon, onNavigate }: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard' || to === '/admin'}
      onClick={onNavigate}
      className={({ isActive }) => cn(
        'group flex items-center gap-2.5 rounded-lg px-3 py-2 transition',
        isActive
          ? 'bg-brand-500/15 text-brand-300 ring-1 ring-inset ring-brand-500/20 dark:bg-primary/10 dark:text-primary dark:ring-primary/20'
          : 'text-ink-200 hover:bg-ink-800 hover:text-white dark:text-foreground/70 dark:hover:bg-secondary dark:hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </NavLink>
  );
}
